import React, { useState, useEffect } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { Button } from "@aix/ui"
import { Plus, Database, FileCode, MessageSquare } from "lucide-react"
import GraphTab from "./GraphTab"
import ChatSidebar from "./components/ChatSidebar"
import ImportModal from "./components/ImportModal"
import { trpc } from "../../lib/trpc"

const DatabrainScreen: React.FC = () => {
  const [selectedSystemId, setSelectedSystemId] = useState<string | null>(null)
  const [showNewSystemModal, setShowNewSystemModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)

  // Queries
  const { data: systems, refetch: refetchSystems } = trpc.databrain.listSystems.useQuery()

  // Auto-select first system when systems load
  useEffect(() => {
    if (systems && systems.length > 0 && !selectedSystemId) {
      setSelectedSystemId(systems[0].id)
    }
  }, [systems, selectedSystemId])

  // Keyboard shortcuts
  useHotkeys("c", () => setIsChatOpen((prev) => !prev), { enableOnFormTags: false })
  useHotkeys("i", () => setShowImportModal(true), { enableOnFormTags: false })

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-background px-6 py-4">
        <h1 className="text-2xl font-bold text-foreground">Database Explorer</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Explore, document, and understand relational data across your systems
        </p>
      </div>

      {/* System Selector Bar */}
      <div className="border-b border-border bg-secondary px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">System:</span>
            <select
              value={selectedSystemId || ""}
              onChange={(e) => setSelectedSystemId(e.target.value || null)}
              className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground min-w-[200px]"
            >
              <option value="">Select a system...</option>
              {systems?.map((system) => (
                <option key={system.id} value={system.id}>
                  {system.name} ({system.type})
                </option>
              ))}
            </select>
            <Button onClick={() => setShowNewSystemModal(true)} size="sm" variant="outline">
              <Plus className="w-3 h-3 mr-1" />
              New System
            </Button>
            <Button onClick={() => setShowImportModal(true)} size="sm" variant="outline" disabled={!selectedSystemId}>
              <FileCode className="w-3 h-3 mr-1" />
              Import Schema
              <span className="ml-1 text-xs opacity-50">i</span>
            </Button>
            {selectedSystemId && (
              <span className="text-xs text-muted-foreground ml-2">
                {systems?.find((s) => s.id === selectedSystemId)?.description}
              </span>
            )}
          </div>
          <Button onClick={() => setIsChatOpen((prev) => !prev)} size="sm" variant={isChatOpen ? "default" : "outline"}>
            <MessageSquare className="w-3 h-3 mr-1" />
            Chat
            <span className="ml-1 text-xs opacity-50">c</span>
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Graph Canvas */}
        <div className="flex-1 relative">
          <GraphTab selectedSystemId={selectedSystemId} />
        </div>

        {/* Chat Sidebar */}
        <ChatSidebar isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} selectedSystemId={selectedSystemId} />
      </div>

      {/* Import Modal */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        selectedSystemId={selectedSystemId}
      />
    </div>
  )
}

export default DatabrainScreen
